import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { WIZARD_SCREEN_TYPES } from '../../constants'
import { WORKFLOW, SECTIONS } from './config'
import WizardDialog from '../../components/WizardDialog'
import GroomingProfileWizardContext, { initialGroomingSetupValues } from './GroomingProfileWizardContext'
import useWizardRouterHistory from '../../hooks/useWizardRouterHistory'
import SectionNavigator from '../../components/SectionNavigator'
import ExitWizardDialog from '../../components/ExitWizardDialog'
import useGroomingProfile from './hooks/useGroomingProfile'
import { GROOMING_PROFILE_KEYS, SHEDDING_AMOUNT_TYPES, GROOMING_ADDONS } from './constants'
import BreedsContext from './BreedsContext'

export const ENTRY_POINTS = {
	pet: 'pet',
	create_appointment: 'create_appointment'
}

const GroomingProfileWizard = ({ step = 0, open = false, onClose, petDetails = {}, from = ENTRY_POINTS.pet }) => {
	const [currentStep, setCurrentStep] = useState(step)
	const [canProceedFunc, setCanProceedFunc] = useState(() => () => true)
	const [showExitWizardDialog, setShowExitWizardDialog] = useState(false)

	const { loading, update, fetchBreeds, facility } = useGroomingProfile()
	const { setStepInHistory, clearHistory, goToNewGroomingAppointmentView } = useWizardRouterHistory()
	const user = useSelector((state) => state.general.user.user)

	// Context Related State -----------------------------------------------------
	const setWizardAttributeAndValues = (newValues) => {
		setWizardContextProvider({
			wizard_state: { ...newValues },
			setWizardAttributeAndValues
		})
	}

	const [wizardContextProvider, setWizardContextProvider] = useState({
		wizard_state: initialGroomingSetupValues,
		setWizardAttributeAndValues
	})

	const setBreeds = (apiValues) => {
		setBreedsContextProvider({
			breeds: { ...apiValues },
			setBreeds
		})
	}

	const [breedsContextProvider, setBreedsContextProvider] = useState({
		breeds: {},
		setBreeds
	})
	// ---------------------------------------------------------------------------

	const REQUIRED_WORKFLOW = useMemo(() => {
		const { wizard_state } = wizardContextProvider
		const filteredWorkflow = WORKFLOW.filter((step) => {
			const { display_step_condition } = step
			if (!display_step_condition || display_step_condition(wizard_state)) {
				return step
			}
		})
		return filteredWorkflow
	}, [wizardContextProvider])

	const requiredSteps = useMemo(() => REQUIRED_WORKFLOW.length, [REQUIRED_WORKFLOW])

	useEffect(() => {
		const { wizard_state, setWizardAttributeAndValues } = wizardContextProvider
		if (petDetails) {
			const updatedValues = { ...wizard_state }
			for (const key in petDetails) {
				if (updatedValues[key] !== undefined) {
					updatedValues[key] = petDetails[key]
					if (key === GROOMING_PROFILE_KEYS.shedding) {
						if (petDetails[key] !== SHEDDING_AMOUNT_TYPES.none) {
							const [shedding_amount, shedding_frequency] =
								petDetails[GROOMING_PROFILE_KEYS.shedding].split('-')
							updatedValues[GROOMING_PROFILE_KEYS.shedding_amount] = shedding_amount.trim()
							updatedValues[GROOMING_PROFILE_KEYS.shedding_frequency] = shedding_frequency.trim()
							updatedValues[GROOMING_PROFILE_KEYS.grooming_addons] = [GROOMING_ADDONS.shed_control]
						} else {
							updatedValues[GROOMING_PROFILE_KEYS.shedding_amount] = petDetails[key]
							updatedValues[GROOMING_PROFILE_KEYS.shedding_frequency] = ''
							updatedValues[GROOMING_PROFILE_KEYS.grooming_addons] = []
						}
					}
				}
			}
			setWizardAttributeAndValues(updatedValues)
		}
	}, [petDetails])

	useEffect(() => {
		const getBreeds = async () => {
			const values = await fetchBreeds()
			breedsContextProvider.setBreeds(values)
		}
		getBreeds()
	}, [fetchBreeds])

	useEffect(() => {
		const stepInt = parseInt(step, 10)
		setCurrentStep(stepInt)
	}, [step])

	const userFirstName = useMemo(() => {
		if (!user || !user.displayName) {
			return 'WhoAreYou?'
		}

		const [first] = user.displayName.split(' ')
		return first || 'Skipper'
	}, [user])

	const handleCloseRequest = useCallback(() => {
		setShowExitWizardDialog(true)
	}, [setShowExitWizardDialog])

	const handleCloseRequestCancel = useCallback(() => {
		setShowExitWizardDialog(false)
	}, [setShowExitWizardDialog])

	const handleStart = useCallback(() => {
		setCurrentStep(1)
		setStepInHistory(1)
	}, [setCurrentStep, setStepInHistory])

	const handleExit = useCallback(() => {
		if (onClose) {
			onClose()
		}
	}, [onClose])

	const handleGoToAppointmentCreation = useCallback(() => {
		if (onClose && facility) {
			onClose()
			goToNewGroomingAppointmentView({
				facilityId: facility._id,
				housholdId: petDetails.household_id,
				petId: petDetails._id
			})
		}
	}, [goToNewGroomingAppointmentView, facility, petDetails])

	const handleOnClose = useCallback(() => {
		if (onClose && !loading) {
			setShowExitWizardDialog(false)
			// clearHistory()
			onClose()
		}
	}, [onClose, loading, setShowExitWizardDialog, clearHistory])

	const canProceed = useMemo(() => {
		return canProceedFunc(wizardContextProvider.wizard_state)
	}, [canProceedFunc, wizardContextProvider])

	/**
	 * returns the configuration object for the current step
	 */
	const currentWorflowStepConfig = useMemo(() => {
		return (
			REQUIRED_WORKFLOW[currentStep] || {
				section: null,
				type: null,
				component: null
			}
		)
	}, [currentStep, REQUIRED_WORKFLOW])

	const hideActionsOtherThanClose = useMemo(() => {
		const { type } = currentWorflowStepConfig
		if (type === WIZARD_SCREEN_TYPES.Start || type === WIZARD_SCREEN_TYPES.Exit) {
			return true
		}
		return false
	}, [currentWorflowStepConfig])

	const hideNext = useMemo(() => {
		const { type } = currentWorflowStepConfig
		if (type === WIZARD_SCREEN_TYPES.Invoice || type === WIZARD_SCREEN_TYPES.Final) {
			return true
		}
		return false
	}, [currentWorflowStepConfig])

	const currentSection = useMemo(() => {
		const { section, type } = currentWorflowStepConfig
		if (type && type === WIZARD_SCREEN_TYPES.Intro) {
			return null
		}

		return section
	}, [currentWorflowStepConfig])

	const currentAttribute = useMemo(() => {
		const { attr } = currentWorflowStepConfig || ''
		return attr
	}, [currentWorflowStepConfig])

	const requiresApiUpdate = useMemo(() => {
		const { requires_api_update } = currentWorflowStepConfig || false
		if (requires_api_update) {
			return requires_api_update(wizardContextProvider.wizard_state)
		}
	}, [currentWorflowStepConfig, wizardContextProvider])

	const handleUpdate = useCallback(async () => {
		const { _id } = petDetails || null
		const { wizard_state } = wizardContextProvider
		const value = wizard_state[currentAttribute]
		if (value !== undefined && requiresApiUpdate) {
			switch (currentAttribute) {
				case GROOMING_PROFILE_KEYS.shedding_amount:
					if (wizard_state[GROOMING_PROFILE_KEYS.shedding_amount] === SHEDDING_AMOUNT_TYPES.none) {
						await update(_id, {
							[GROOMING_PROFILE_KEYS.shedding]: SHEDDING_AMOUNT_TYPES.none,
							[GROOMING_PROFILE_KEYS.grooming_addons]: []
						})
					}
					break
				case GROOMING_PROFILE_KEYS.shedding_frequency:
					await update(_id, {
						[GROOMING_PROFILE_KEYS.shedding]: `${
							wizard_state[GROOMING_PROFILE_KEYS.shedding_amount]
						} - ${wizard_state[GROOMING_PROFILE_KEYS.shedding_frequency]}`,
						[GROOMING_PROFILE_KEYS.grooming_addons]: [GROOMING_ADDONS.shed_control]
					})
					break
				case GROOMING_PROFILE_KEYS.breed_mix:
					await update(_id, {
						[GROOMING_PROFILE_KEYS.breed_mix]: wizard_state[GROOMING_PROFILE_KEYS.breed_mix],
						[GROOMING_PROFILE_KEYS.breeds]: wizard_state[GROOMING_PROFILE_KEYS.breeds],
						[GROOMING_PROFILE_KEYS.coat_type]: wizard_state[GROOMING_PROFILE_KEYS.coat_type],
						[GROOMING_PROFILE_KEYS.coat_length]: wizard_state[GROOMING_PROFILE_KEYS.coat_length],
						[GROOMING_PROFILE_KEYS.grooming_level]: wizard_state[GROOMING_PROFILE_KEYS.grooming_level],
						[GROOMING_PROFILE_KEYS.allowed_grooming_services]:
							wizard_state[GROOMING_PROFILE_KEYS.allowed_grooming_services]
					})
					break
				case GROOMING_PROFILE_KEYS.breeds:
					await update(_id, {
						[GROOMING_PROFILE_KEYS.breeds]: wizard_state[GROOMING_PROFILE_KEYS.breeds],
						[GROOMING_PROFILE_KEYS.coat_type]: wizard_state[GROOMING_PROFILE_KEYS.coat_type],
						[GROOMING_PROFILE_KEYS.coat_length]: wizard_state[GROOMING_PROFILE_KEYS.coat_length],
						[GROOMING_PROFILE_KEYS.grooming_level]: wizard_state[GROOMING_PROFILE_KEYS.grooming_level],
						[GROOMING_PROFILE_KEYS.allowed_grooming_services]:
							wizard_state[GROOMING_PROFILE_KEYS.allowed_grooming_services]
					})
					break
				case GROOMING_PROFILE_KEYS.coat_type:
					await update(_id, {
						[GROOMING_PROFILE_KEYS.coat_type]: wizard_state[GROOMING_PROFILE_KEYS.coat_type],
						[GROOMING_PROFILE_KEYS.coat_length]: wizard_state[GROOMING_PROFILE_KEYS.coat_length],
						[GROOMING_PROFILE_KEYS.grooming_level]: wizard_state[GROOMING_PROFILE_KEYS.grooming_level],
						[GROOMING_PROFILE_KEYS.allowed_grooming_services]:
							wizard_state[GROOMING_PROFILE_KEYS.allowed_grooming_services]
					})
					break
				default:
					await update(_id, { [currentAttribute]: value })
					break
			}
		}
	}, [wizardContextProvider, currentAttribute, requiresApiUpdate, update, petDetails])

	const incrementStep = useCallback(
		async (params = { by: 1, withSave: true }) => {
			const curStep = parseInt(currentStep, 10)
			if (curStep !== requiredSteps) {
				if (params.withSave) {
					await handleUpdate()
				}
				const goToStep = curStep + params.by
				setCurrentStep(goToStep)
				setStepInHistory(goToStep)
			}
		},
		[currentStep, setCurrentStep, setStepInHistory, requiredSteps, handleUpdate]
	)

	const decrementStep = useCallback(
		async (params = { by: 1, withSave: true }) => {
			const curStep = parseInt(currentStep, 10)
			if (curStep !== 0) {
				if (params.withSave) {
					await handleUpdate()
				}
				const goToStep = curStep - params.by
				setCurrentStep(goToStep)
				setStepInHistory(goToStep)
			}
		},
		[currentStep, setCurrentStep, setStepInHistory, handleUpdate]
	)

	const currentComponent = useMemo(() => {
		const { component, can_proceed_condition, attr, type } = currentWorflowStepConfig
		if (!component) {
			return <div>I dont seem to have that step!</div>
		}

		if (can_proceed_condition) {
			setCanProceedFunc(() => can_proceed_condition)
		} else {
			setCanProceedFunc(() => () => true)
		}
		const props = {}
		if (type === WIZARD_SCREEN_TYPES.Start) {
			props['onAction'] = handleStart
		}
		if (type === WIZARD_SCREEN_TYPES.Exit) {
			props['onAction'] = handleExit
			if (from === ENTRY_POINTS.pet) {
				props['onSecondaryAction'] = handleGoToAppointmentCreation
			}
		}
		if (attr) {
			props['attr'] = attr
		}

		props['petName'] = petDetails.name
		props['userName'] = userFirstName
		props['from'] = from

		return component(props)
	}, [currentWorflowStepConfig, userFirstName, petDetails, handleStart, handleExit])

	return (
		<GroomingProfileWizardContext.Provider value={{ wizardContextProvider }}>
			<BreedsContext.Provider value={{ breedsContextProvider }}>
				<div>
					<WizardDialog
						open={open}
						hideBack={hideActionsOtherThanClose}
						hideFooter={hideActionsOtherThanClose || hideNext}
						disableNext={!canProceed || loading}
						disableBack={loading}
						onClose={handleCloseRequest}
						onNext={incrementStep}
						onBack={decrementStep}
						loading={loading}>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								flex: 1,
								gap: '1rem',
								opacity: loading ? 0.5 : 1
							}}>
							{currentSection ? (
								<SectionNavigator sections={SECTIONS} activeSectionId={currentSection} />
							) : null}
							{currentComponent}
						</div>
					</WizardDialog>
					<ExitWizardDialog
						title="Exit Wizard?"
						description="Are you sure you want to exit the wizard? You may never be able to return!"
						open={showExitWizardDialog}
						onExit={handleOnClose}
						onCancel={handleCloseRequestCancel}
					/>
				</div>
			</BreedsContext.Provider>
		</GroomingProfileWizardContext.Provider>
	)
}

export default GroomingProfileWizard
